import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { DownloadSimple, X, DeviceMobile, Share, Plus, DotsThreeVertical } from '@phosphor-icons/react';
import { toast } from 'sonner';

const IS_IOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
const IS_ANDROID = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const IS_STANDALONE = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (IS_STANDALONE) { setInstalled(true); return; }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!sessionStorage.getItem('pwa-toast-shown')) {
        setShowToast(true);
        sessionStorage.setItem('pwa-toast-shown', '1');
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setShowModal(false);
      setShowToast(false);
      toast.success('Pow Wow Finder installed — open it from your home screen!');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS auto-toast after 4s (Apple does not fire beforeinstallprompt)
    if (IS_IOS && !sessionStorage.getItem('pwa-toast-shown')) {
      const t = setTimeout(() => {
        setShowToast(true);
        sessionStorage.setItem('pwa-toast-shown', '1');
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onPrompt);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    // 1. Android / Chrome / Edge — native one-tap install (BEST CASE)
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === 'accepted') {
          setShowModal(false);
          setShowToast(false);
        }
        setDeferred(null);
        return;
      } catch {
        // fall through to manual
      }
    }

    // 2. iOS Safari — open the native Share Sheet (closest to "automatic" Apple allows).
    // From there the user scrolls down and taps "Add to Home Screen".
    if (IS_IOS && navigator.share) {
      try {
        await navigator.share({
          title: 'Pow Wow Finder',
          text: 'Add Pow Wow Finder to your home screen',
          url: window.location.origin,
        });
        // After share dismisses, also show the visual modal so the user knows what to tap next
        setShowModal(true);
        setShowToast(false);
        return;
      } catch {
        // User cancelled share — still show modal
      }
    }

    // 3. Fallback — show illustrated step-by-step modal
    setShowModal(true);
    setShowToast(false);
  };

  const dismiss = () => { setShowToast(false); };
  const closeModal = () => setShowModal(false);

  if (installed) return null;

  return (
    <>
      {/* Always-visible "Install app" pill — bottom-left, above chat launcher */}
      <button
        onClick={triggerInstall}
        className="fixed bottom-6 left-6 z-40 bg-card border border-turquoise/40 hover:border-turquoise text-foreground rounded-full pl-4 pr-5 py-2.5 shadow-2xl flex items-center gap-2 hover:scale-105 transition-all group"
        data-testid="pwa-install-launcher"
        aria-label="Install Pow Wow Finder app"
      >
        <DeviceMobile size={18} weight="duotone" className="text-turquoise" />
        <span className="text-sm font-semibold">Install app</span>
      </button>

      {/* Auto-show toast (one-time per session) */}
      {showToast && (
        <div className="fixed bottom-24 left-6 z-40 max-w-sm bg-card border border-turquoise/40 rounded-2xl p-4 shadow-2xl shadow-turquoise/10 fade-up" data-testid="pwa-install-toast">
          <button onClick={dismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" aria-label="Dismiss">
            <X size={16} />
          </button>
          <div className="flex items-start gap-3 pr-4">
            <img src="/icon-192.png" alt="" className="w-12 h-12 flex-shrink-0 rounded-xl" />
            <div className="flex-1">
              <p className="font-display text-base mb-1">Install Pow Wow Finder</p>
              <p className="text-xs text-muted-foreground mb-3">
                {deferred
                  ? 'One tap to add the app to your home screen.'
                  : IS_IOS
                    ? "We'll open the Share menu — tap 'Add to Home Screen' to install."
                    : 'Add the app to your home screen for the full experience.'}
              </p>
              <Button onClick={triggerInstall} size="sm" className="bg-turquoise text-navy hover:bg-turquoise/90 rounded-full font-semibold" data-testid="pwa-install-btn">
                <DownloadSimple size={14} className="mr-1.5" weight="bold" />
                {deferred ? 'Install now' : (IS_IOS ? 'Add to Home Screen' : 'How to install')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual install instructions modal — illustrated, big icons */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal} data-testid="pwa-install-modal">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full p-8 relative" onClick={e => e.stopPropagation()}>
            <button onClick={closeModal} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" aria-label="Close">
              <X size={20} />
            </button>
            <img src="/icon-192.png" alt="" className="w-16 h-16 mb-4 rounded-2xl" />
            <h2 className="font-display text-2xl mb-1">Add to home screen</h2>
            <p className="text-sm text-muted-foreground mb-6">
              {IS_IOS
                ? 'Apple Safari requires one extra tap from you — this is a privacy restriction Apple enforces on every website.'
                : 'A few taps and Pow Wow Finder lives on your home screen.'}
            </p>

            {IS_IOS ? (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">1</span>
                  <span className="flex-1">Tap the <Share size={18} weight="bold" className="inline text-turquoise mx-1" /> <strong className="text-turquoise">Share</strong> icon at the bottom of Safari.</span>
                </li>
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">2</span>
                  <span className="flex-1">Scroll down and tap <Plus size={18} weight="bold" className="inline text-turquoise mx-1" /> <strong className="text-turquoise">"Add to Home Screen"</strong>.</span>
                </li>
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">3</span>
                  <span className="flex-1">Tap <strong className="text-turquoise">Add</strong> in the top-right corner. Done.</span>
                </li>
                <li className="text-xs text-muted-foreground italic mt-3 px-1">
                  Tip: this only works in <strong>Safari</strong>. If you're in Chrome on iPhone, copy the URL and open it in Safari first.
                </li>
              </ol>
            ) : IS_ANDROID ? (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">1</span>
                  <span className="flex-1">Tap the <DotsThreeVertical size={18} weight="bold" className="inline text-turquoise mx-1" /> <strong className="text-turquoise">menu</strong> at the top-right of Chrome.</span>
                </li>
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">2</span>
                  <span className="flex-1">Tap <strong className="text-turquoise">"Install app"</strong> or <strong className="text-turquoise">"Add to Home screen"</strong>.</span>
                </li>
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">3</span>
                  <span className="flex-1">Tap <strong className="text-turquoise">Install</strong>. The app icon appears on your home screen.</span>
                </li>
                <li className="text-xs text-muted-foreground italic mt-3 px-1">
                  Tip: works in Chrome, Edge, Samsung Internet, and Brave. Firefox uses "Install" from the same menu.
                </li>
              </ol>
            ) : (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">1</span>
                  <span className="flex-1">Look for the <Plus size={18} weight="bold" className="inline text-turquoise mx-1" /> install icon in the right side of your address bar (Chrome, Edge, Brave).</span>
                </li>
                <li className="flex items-center gap-3 bg-navy/40 rounded-xl p-3">
                  <span className="bg-turquoise text-navy rounded-full w-7 h-7 flex-shrink-0 grid place-items-center font-bold text-xs">2</span>
                  <span className="flex-1">Click it and choose <strong className="text-turquoise">Install</strong>.</span>
                </li>
                <li className="text-xs text-muted-foreground italic mt-3 px-1">
                  For the full mobile experience, open this site on your phone instead.
                </li>
              </ol>
            )}

            <Button onClick={closeModal} className="w-full mt-6 bg-turquoise text-navy hover:bg-turquoise/90 rounded-full" data-testid="pwa-install-modal-close">
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
